import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import { getOnlineNode } from "../core/getNode";
import { postNode } from "../core/postNode";
import CompanyappTags from "../screens/account/companyappTags";

const TAGS_BASE = "api/companyapp/tags";

function TagsPicker(
  {
    value = [],
    onChange,
    label = "Tags",
    disabled = false,
    showManageLink = true,
  },
  ref,
) {
  const user = useSelector((state) => state).authReducer.user;

  const [options, setOptions] = useState([]); // [{id, name}, …]
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showManager, setShowManager] = useState(false);

  // Normalize incoming value into a list of {id, name} objects.
  const selected = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value
      .map((v) => {
        if (v && typeof v === "object" && "id" in v) {
          return { id: Number(v.id), name: v.name ?? "" };
        }
        const id = Number(v);
        if (!id) return null;
        const match = options.find((o) => o.id === id);
        return { id, name: match ? match.name : `#${id}` };
      })
      .filter(Boolean);
  }, [value, options]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getOnlineNode(
        `${TAGS_BASE}?_format=json&items_per_page=1000`,
      );
      setOptions(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      // Picker stays empty; user can still create via free-text.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.current_user) refresh();
  }, [user?.current_user, refresh]);

  // Allow a parent to imperatively refetch the autocomplete options - e.g.
  // after closing an externally-opened tag manager modal.
  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  const emit = (next) => {
    if (typeof onChange === "function") {
      onChange(next.map((t) => t.id));
    }
  };

  const handleChange = async (_event, nextValues) => {
    const created = [];
    const out = [];

    for (const item of nextValues) {
      if (typeof item === "string") {
        const trimmed = item.trim();
        if (!trimmed) continue;
        const match = options.find(
          (o) => o.name.toLowerCase() === trimmed.toLowerCase(),
        );
        if (match) {
          out.push(match);
          continue;
        }
        try {
          setCreating(true);
          const { data } = await postNode(
            TAGS_BASE,
            { name: trimmed, description: "" },
            user.csrf_token,
          );
          if (data && data.id) {
            const created_tag = { id: Number(data.id), name: data.name };
            created.push(created_tag);
            out.push(created_tag);
          }
        } catch (e) {
          // Conflict (existing user tag) or network error — silently drop the
          // free-typed entry. The autocomplete options refresh below will
          // surface any pre-existing match next time.
        } finally {
          setCreating(false);
        }
        continue;
      }
      if (item && typeof item === "object" && item.id) {
        out.push({ id: Number(item.id), name: item.name ?? "" });
      }
    }

    if (created.length) {
      setOptions((prev) => {
        const existingIds = new Set(prev.map((o) => o.id));
        const merged = [
          ...prev,
          ...created.filter((c) => !existingIds.has(c.id)),
        ];
        merged.sort((a, b) => a.name.localeCompare(b.name));
        return merged;
      });
    }

    const seen = new Set();
    const deduped = out.filter((t) => {
      if (seen.has(t.id)) return false;
      seen.add(t.id);
      return true;
    });
    emit(deduped);
  };

  const handleManagerClose = () => {
    setShowManager(false);
    // Refresh in case the user renamed/deleted tags via the manager modal.
    refresh();
  };

  return (
    <Box>
      {label && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="form-group-label" owner="admin">
            {label}
          </Typography>
          {showManageLink && (
            <Button
              size="small"
              variant="text"
              startIcon={<SettingsOutlinedIcon fontSize="small" />}
              onClick={() => setShowManager(true)}
              disabled={disabled}
              sx={{ textTransform: "none" }}
            >
              Manage tags
            </Button>
          )}
        </Stack>
      )}

      <Autocomplete
        multiple
        freeSolo
        size="small"
        disableClearable
        disabled={disabled || loading}
        options={options}
        value={selected}
        loading={loading || creating}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name ?? ""
        }
        isOptionEqualToValue={(option, val) =>
          option && val && Number(option.id) === Number(val.id)
        }
        filterSelectedOptions
        onChange={handleChange}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={
              selected.length
                ? "Add another tag…"
                : "Type to search or create a tag"
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(loading || creating) && (
                    <CircularProgress size={14} sx={{ mr: 1 }} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {selected.length > 0 && (
        <Box
          sx={{
            mt: 5,
            mb: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {selected.map((tag) => (
            <Chip
              key={tag.id}
              icon={
                <LocalOfferOutlinedIcon
                  fontSize="small"
                  sx={{ fontSize: 20 }}
                />
              }
              label={tag.name}
              onDelete={
                disabled
                  ? undefined
                  : () => emit(selected.filter((t) => t.id !== tag.id))
              }
              sx={{
                padding: "10px 4px",
                fontWeight: 400,
              }}
            />
          ))}
        </Box>
      )}

      {showManager && (
        <CompanyappTags
          asModal
          open={showManager}
          onClose={handleManagerClose}
        />
      )}
    </Box>
  );
}

export default forwardRef(TagsPicker);
